import './SequenceNode.css';
import React, { FC, useContext, RefObject } from "react";
import { observer } from 'mobx-react-lite';
import cx from 'classnames';
import icons from '../../../../icons';
import { LeftPaneContext } from '../LeftPaneContext';
import { LeftPaneSelectedSection } from '../../../../constants';
import { TreeNode, TreeNodeLabel } from './TreeNode';
import { EditableLabel } from "../../../../components/EditableLabel";
import { SequenceContextMenu } from "./SequenceContextMenu";
import { InstructionNodeFactory } from "./instructionNodes";
import { ApplicationTypes, InstructionStateStatus, SequenceState } from "../../../../machinelogic/constants";
import { ApplicationState } from "../../../../machinelogic";

interface RootTitleProps {
    sequence: any;
    isInitialSequence: boolean;
    parentRef: RefObject<HTMLDivElement>;
    isSelected: boolean;
    onClick: () => void;
  }

const RootTitle: FC<RootTitleProps> = observer(({sequence, isInitialSequence, parentRef, isSelected, onClick}) => {
  const [isEditingSequenceName, setIsEditingSequenceName] = React.useState(false);

  let sideElement = <></>;

  const isApplicationInactive = sequence.mApp.state !== ApplicationState.RUNNING &&
    sequence.mApp.state !== ApplicationState.PAUSED;

  const sequenceIsLocked = sequence.getIsLocked();
  const isInitialMachineAppSequence = sequence.getApp().getType() === ApplicationTypes.MachineApp && isInitialSequence;


  if (isApplicationInactive) {
    if (sequenceIsLocked) {
      sideElement = <div><i className={icons.locked}></i></div>;
    }
    else if (isInitialMachineAppSequence) {
      sideElement = <div></div>;
    }
    else {
      sideElement = (
        <SequenceContextMenu
          sequence={sequence}
          toggleTextInput={() => setIsEditingSequenceName(!isEditingSequenceName)}
          scrollingContainerRef={parentRef}
        />
      );
    }
  }
  else {
    if (sequence.state === SequenceState.RUNNING) {
      sideElement = <div className="sequence-state-indicator ml_animated_background_tree running"><i className={icons.play}></i></div>;
    }
    if (sequence.state === SequenceState.PAUSED) {
      sideElement = <div className="sequence-state-indicator ml_animated_background_tree paused"><i className={icons.pause}></i></div>;
    }
    if (sequence.state === SequenceState.WAITING) {
      sideElement = <div className="sequence-state-indicator ml_animated_background_tree waiting"><i className={icons.waitFor}></i></div>;
    }
  }

  return (
    <div
      className={cx('root-title', {
        'root-title-selected': isSelected,
        'is-editing': isEditingSequenceName
      })}
    >
      <TreeNodeLabel
        icon={isInitialSequence ? icons.sequence : icons.childSequence}
        name={
          <EditableLabel
            isEditing={isEditingSequenceName}
            setIsEditing={setIsEditingSequenceName}
            label={sequence.name}
            value={sequence.name}
            setValue={(value) => sequence.setName(value)}
          />
        }
        onClick={onClick}
      />
      {sideElement}
    </div>
  );
});

interface SequenceNodeProps {
    sequence: any;
    isSelected: boolean;
    isInitialSequence: boolean;
    parentRef: RefObject<HTMLDivElement>;
}

export const SequenceNode: FC<SequenceNodeProps> = observer(({sequence, isSelected, isInitialSequence, parentRef}) => {

  const { library, openSequence, invalidInstructionUuids, setSelectedSection } = useContext(LeftPaneContext);

  const selectedInstruction = sequence.selectedInstruction;
  const hasInstructionsWithErrors = sequence.instructions.some((instruction) => invalidInstructionUuids?.includes(instruction.uuid));

  // this is sort of useless now but will be useful when we have multiple initial sequences
  const handleOpenSequence = () => {
    const currentApplication = library.currentApplication;
    if(currentApplication.isReadOnly()) { return; }
    setSelectedSection(isInitialSequence ? LeftPaneSelectedSection.MAIN_PROGRAM : LeftPaneSelectedSection.SEQUENCES);
    openSequence(library, currentApplication, sequence);
    currentApplication.selectSequenceByName(sequence.name);
  };

  return (
    <TreeNode
      title={ <RootTitle
        sequence={ sequence }
        isInitialSequence={ isInitialSequence }
        parentRef={ parentRef }
        onClick={ handleOpenSequence }
        isSelected={ isSelected }
      />
      }
      id={sequence.uuid}
      keepOpenState
      root
      isCollapsible
      isSelected={isSelected}
      isDefaultExpanded={isInitialSequence || hasInstructionsWithErrors}
    >
      {sequence.instructions.map((instruction) => {

        const isSelected = selectedInstruction?.uuid === instruction.uuid;
        const isInvalid = invalidInstructionUuids?.includes(instruction.uuid);

        const handleInstructionClick = () => {
          if(library.currentApplication.isReadOnly()) { return; }

          handleOpenSequence();
          sequence.setSelectedInstruction(instruction);

          // move to right pane, when right pane is react
          library.m3DContext?.robotAppsService?.send('MachineLogic/treeView/instructionElement/INSTRUCTION_CLICKED', { instruction });
        };

        return <InstructionNodeFactory
          key={instruction.uuid}
          instruction={instruction}
          titleClassName={ cx({
            'ml_animated_background_tree running': instruction.state.status === InstructionStateStatus.RUNNING,
            'ml_animated_background_tree waiting': instruction.state.status === InstructionStateStatus.WAITING,
            'ml_animated_background_tree paused': instruction.state.status === InstructionStateStatus.PAUSED
          }) }
          isSelected={isSelected}
          isInvalid={isInvalid}
          onClick={handleInstructionClick}
        />;
      })}

    </TreeNode>
  );
});